import { Button, Card, Figure, Image, Table } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { GameModel } from '../../backend/models/gameModel';
import { checkGameWinCondition, checkMatchWinCondition } from '../../logic/game/game';
import { addPointToGame, RequestToAddPointToGame } from '../../logic/game/AddPointToGame';
import '../component.css';
import { useParams } from 'react-router-dom';
import MatchSettings from '../matchSettings/MatchSettings';
import { getMatchData } from '../../backend/entryPoints/getMatchDataFromServer';
import { MatchViewModel, MatchViewModelSchema } from '../../backend/entryPoints/mapper/mapToMatchViewModel';
import { getFieldAllocation } from '../../backend/entryPoints/getFieldAllocation';
import { saveMatchData } from '../../backend/entryPoints/saveMatchDataToServer';

import SettingsIcon from '../../images/settings.png';
import UndoIcon from '../../images/undo.png';
import ShuttleImage from '../../images/shuttle.png';
import MatchEndedModal from '../matchEndedModal/MatchEndedModal';
import IntervalModal from '../intervalModal/IntervalModal';
import ServiceImage from '../../images/service.png';
import OtherFieldScoreInfo from '../otherFieldScoreInfo/OtherFieldScoreInfo';
import { useTranslation } from 'react-i18next';

// Data that are necessary for the scoreboard
// - Players
// - Games (all games with score, etc.)
// - Game mode
// - Positioning of the players
// - Time (optional)

function Scoreboard() {
    const { side } = useParams();
    const { t } = useTranslation();

    const [cache, setCache] = useState<MatchViewModel[]>();
    const [matchMode, setMatchMode] = useState<string>('');
    const [standing, setStanding] = useState<number[]>([0, 0]);
    const [match, setMatch] = useState<MatchViewModel>();
    const [games, setGames] = useState<GameModel[]>();
    const [score, setScore] = useState<number[]>([0, 0]);
    const [playerPosition, setPlayerPosition] = useState<number[][]>([
        [0, 1],
        [2, 3],
    ]);
    const [servingSide, setServingSide] = useState<string>('left');
    const [showSettings, setShowSettings] = useState<boolean>(true);
    const [showMatchEnded, setShowMatchEnded] = useState<boolean>(false);
    const [gameEnded, setGameEnded] = useState<boolean>(false);
    const [shuttlePosition, setShuttlePosition] = useState<number>(-1);
    const [interval, setInterval] = useState<boolean>(false);
    const [showIntervalModal, setShowIntervalModal] = useState<boolean>(false);

    useEffect(() => {
        loadCurrentFieldAllocationAndMatch();
    }, []);

    useEffect(() => {
        if (match === undefined) return;
        if (standing[0] === 2 || standing[1] === 2) setShowMatchEnded(true);
    }, [standing]);

    const loadCurrentFieldAllocationAndMatch = () => {
        setScore([0, 0]);
        setStanding([0, 0]);
        setGames(undefined);
        if (side === undefined) {
            window.alert('Field is not set. Terminal error. Please contact the developer');
            return;
        }
        getFieldAllocation(side)
            .then((response) => response.text().then((data) => loadMatchDataFromStorage(data)))
            .catch((err) => console.error(err));
    };

    const loadMatchDataFromStorage = (matchName: string) => {
        getMatchData(matchName)
            .then((response) =>
                response.text().then((body) => mapAndSetMatch(MatchViewModelSchema.parse(JSON.parse(body)), true))
            )
            .catch((err) => console.error(`Unexpected error occurred: ${err}`));
    };

    const mapAndSetMatch = (matchViewModel: MatchViewModel, init: boolean = false) => {
        setMatch(matchViewModel);
        setPlayerPosition(matchViewModel.player_positioning);
        setServingSide(matchViewModel.serving_team === 0 ? 'left' : 'right');
        setShuttlePosition(matchViewModel.server ?? 0);
        const currentGame =
            matchViewModel.games !== undefined && matchViewModel.games.length > 0
                ? matchViewModel.games[matchViewModel.games.length - 1]
                : { home_points: 0, guest_points: 0 };
        setScore([currentGame.home_points, currentGame.guest_points]);
        setGames(
            matchViewModel.games
                .slice(0, matchViewModel.games.length - 1)
                .map((g) => ({ homePoints: g.home_points, guestPoints: g.guest_points })) ?? undefined
        );
        if (init) setCache([matchViewModel]);
        if (matchViewModel.games.length > 0) {
            const { standing } = checkMatchWinCondition(
                matchViewModel.games.map((g) => ({ homePoints: g.home_points, guestPoints: g.guest_points }))
            );
            setStanding(standing);
            setGameEnded(standing[0] === 2 || standing[1] === 2);
        }
    };

    const undoChange = () => {
        if (cache === undefined || cache.length === 0) {
            window.alert('No cached match data available to undo.');
            return;
        }
        const lastMatchState = cache[cache.length - 1];
        setCache(cache.slice(0, cache.length - 1));
        mapAndSetMatch(lastMatchState);
        saveMatchData(lastMatchState)
            .then((r) => console.info(`Response for saving match data: ${r}`))
            .catch((err) => {
                window.alert('Error saving match data: ' + err.message);
            });
    };

    const determineServerAndReceiverOfCurrentPosition = (
        playerPositioning: number[][],
        game: GameModel,
        side: 'left' | 'right'
    ) => {
        if (side === 'left') {
            return {
                server: playerPositioning[0][(game.homePoints + 1) % 2],
                receiver: playerPositioning[1][game.homePoints % 2],
            };
        }
        return {
            server: playerPositioning[1][game.guestPoints % 2],
            receiver: playerPositioning[0][(game.guestPoints + 1) % 2],
        };
    };

    // requirements:
    // - interval settings (server and receiver must open automatically after a finished game)
    // - interval settings (change of server and receiver should not overwrite the whole game state)
    // - undo function

    const handleClickOnSide = (side: 'left' | 'right') => {
        if (gameEnded) {
            setShowMatchEnded(true);
            return;
        }
        if (match !== undefined) {
            console.log(match);
            setCache(cache !== undefined ? [...cache, match] : [match]);
        }
        if (match === undefined) {
            window.alert('Match is not set. Please configure the match first.');
            return;
        }
        const request: RequestToAddPointToGame = {
            mode: match.mode === 0 ? 'singles' : 'doubles',
            game: { homePoints: score[0], guestPoints: score[1] },
            playerPositioning: playerPosition,
            servingSide,
            scorer: side,
        };
        const { game, playerPositioning } = addPointToGame(request);
        setScore([game.homePoints, game.guestPoints]);
        setServingSide(side);

        let matchInterval = false;
        const isWinConditionReached = checkGameWinCondition([game.homePoints, game.guestPoints]);
        if (isWinConditionReached !== -1) {
            setGames(games !== undefined ? [...games, game] : [game]);
            const { winner, standing } = checkMatchWinCondition([game, ...(games ?? [])]);
            setStanding(standing);
            // If the game finished (one of the two teams achieved to win two games) set the player
            // positioning and save state of the game
            // else a new game starts
            if (winner !== -1) {
                setPlayerPosition(playerPositioning);
                setGameEnded(true);
            } else {
                // reset score for new game
                setScore([0, 0]);
                // special logic in case the game is a singles (there is only one starting position)
                if (match.mode === 0) {
                    setPlayerPosition([
                        [-1, 0],
                        [2, -1],
                    ]);
                }
                matchInterval = true;
            }
        }
        const { server, receiver } = determineServerAndReceiverOfCurrentPosition(playerPositioning, game, side);
        const newMatchState: MatchViewModel = {
            ...match,
            games:
                games !== undefined
                    ? [
                          ...games?.map((game) => ({ home_points: game.homePoints, guest_points: game.guestPoints })),
                          {
                              home_points: game.homePoints,
                              guest_points: game.guestPoints,
                          },
                      ]
                    : [{ home_points: game.homePoints, guest_points: game.guestPoints }],
            player_positioning:
                matchInterval && match.mode === 0
                    ? [
                          [-1, 0],
                          [2, -1],
                      ]
                    : playerPositioning,
            server,
            receiver,
            serving_team: side === 'left' ? 0 : 1,
        };
        if (matchInterval) {
            newMatchState.games = [...newMatchState.games, { home_points: 0, guest_points: 0 }];
            setShowIntervalModal(true);
            setInterval(true);
        }
        setShuttlePosition(server);
        setPlayerPosition(
            matchInterval && match.mode === 0
                ? [
                      [-1, 0],
                      [2, -1],
                  ]
                : playerPositioning
        );
        setMatch(newMatchState);
        saveMatchData(newMatchState)
            .then((r) => console.log(`Response for saving match data ${r}`))
            .catch((err) => {
                window.alert('Error saving match data: ' + err.message);
            });
    };

    const setServerAndReceiver = (
        servingSide: number,
        server: number,
        receiver: number,
        playerStartingPosition: number[][]
    ) => {
        if (match === undefined) {
            return;
        }
        const startingPosition =
            match.mode === 0
                ? [
                      [-1, 0],
                      [2, -1],
                  ]
                : playerStartingPosition;
        const newMatchState: MatchViewModel = {
            ...match,
            server,
            receiver,
            serving_team: servingSide,
            player_positioning: startingPosition,
        };
        setServingSide(servingSide === 0 ? 'left' : 'right');
        setPlayerPosition(startingPosition);
        setMatch(newMatchState);
        setShuttlePosition(server);
        saveMatchData(newMatchState)
            .then((r) => console.log(`Response for saving match data ${r}`))
            .catch((err) => {
                window.alert('Error saving match data: ' + err.message);
            });
    };

    const handleMatchUpdate = (matchUpdate: MatchViewModel) => {
        mapAndSetMatch(matchUpdate, true);
    };

    return (
        <div>
            <MatchSettings
                field={side ?? ''}
                interval={interval}
                match={match}
                modeString={match?.mode_string ?? ''} // TODO: needs to be changed in the future
                setMatch={handleMatchUpdate}
                visible={showSettings}
                handleClose={() => {
                    setShowSettings(false);
                }}
            />
            <IntervalModal
                visible={showIntervalModal}
                interval={interval}
                handleClose={() => {
                    setShowIntervalModal(false);
                    setInterval(false);
                }}
                setServerReceiver={setServerAndReceiver}
                match={match}
            />
            <MatchEndedModal
                visible={showMatchEnded}
                handleClose={() => {
                    setShowMatchEnded(false);
                }}
                match={match}
            />
            <Card style={{ margin: '20px' }}>
                <Card.Header>
                    {t('court')} {side}
                </Card.Header>
                <Card.Body>
                    <div>
                        <Table className={'width100'}>
                            <tbody>
                                <tr className={'height8Percentage'}>
                                    <td className={'width40'}>
                                        <OtherFieldScoreInfo field={side === 'left' ? 'right' : 'left'} />
                                    </td>
                                    <td className={'width8 td-score'}>{score[0]}</td>
                                    <td className={'width4 td-score'}>:</td>
                                    <td className={'width8 td-score'}>{score[1]}</td>
                                    <td className={'width40'}>
                                        <div style={{ float: 'right', padding: '0px', border: 'none' }}>
                                            <Button variant="light" onClick={() => setShowSettings(true)}>
                                                <Figure>
                                                    <Figure.Image width={64} height={64} src={SettingsIcon} />
                                                    <Figure.Caption>Settings</Figure.Caption>
                                                </Figure>
                                            </Button>
                                        </div>
                                        <div
                                            style={{
                                                float: 'right',
                                                padding: '0px',
                                                border: 'none',
                                                marginRight: '10px',
                                            }}
                                        >
                                            <Button variant={'light'} onClick={undoChange}>
                                                <Figure>
                                                    <Figure.Image width={64} height={64} src={UndoIcon} />
                                                    <Figure.Caption>Undo</Figure.Caption>
                                                </Figure>
                                            </Button>
                                        </div>
                                        {score[0] === 0 && score[1] === 0 ? (
                                            <div
                                                style={{
                                                    float: 'right',
                                                    padding: '0px',
                                                    border: 'none',
                                                    marginRight: '10px',
                                                }}
                                            >
                                                <Button variant={'light'} onClick={() => setShowIntervalModal(true)}>
                                                    <Figure>
                                                        <Figure.Image height={64} width={64} src={ServiceImage} />
                                                        <Figure.Caption>Change Server/Receiver</Figure.Caption>
                                                    </Figure>
                                                </Button>
                                            </div>
                                        ) : null}
                                    </td>
                                </tr>
                                <tr>
                                    <td className={'width45'} />
                                    <td className={'width4 td-sb-standing'}>{standing[0]}</td>
                                    <td className={'width2 td-sb-standing'}>:</td>
                                    <td className={'width4 td-sb-standing'}>{standing[1]}</td>
                                    <td className={'width45'} />
                                </tr>
                            </tbody>
                        </Table>
                    </div>
                    <div className={'width100'}>
                        <div className={'div-overlaying-left'} onClick={() => handleClickOnSide('left')} />
                        <div className={'div-overlaying-right'} onClick={() => handleClickOnSide('right')} />
                        <Table className={'width100 height60 scoreboard-full-border'}>
                            <tbody>
                                <tr className={'height8Percentage'}>
                                    <td className={'width6 scoreboard-full-border'} />
                                    <td className={'width30 scoreboard-full-border'} />
                                    <td colSpan={2} className={'width28 scoreboard-full-border'} />
                                    <td className={'width30 scoreboard-full-border'} />
                                    <td className={'width6 scoreboard-full-border'} />
                                </tr>
                                <tr className={'height42Percentage'}>
                                    <td className={'width6 scoreboard-full-border'} />
                                    <td
                                        className={'width30 scoreboard-full-border td-players-new'}
                                        id={'td_player_left_1'}
                                        style={{ verticalAlign: 'middle' }}
                                    >
                                        {match !== undefined && playerPosition !== undefined
                                            ? match?.player_home[playerPosition[0][0]]
                                            : 'Player left 1'}
                                        <Image
                                            src={ShuttleImage}
                                            id={'image-playerL1'}
                                            className={
                                                playerPosition !== undefined
                                                    ? shuttlePosition === playerPosition[0][0]
                                                        ? 'shuttle'
                                                        : 'not-shuttle'
                                                    : 'not-shuttle'
                                            }
                                            style={{ float: 'right', transform: 'scale(-1, 1)' }}
                                        />
                                    </td>
                                    <td colSpan={2} className={'width28 scoreboard-top-border'} />
                                    <td
                                        className={'width30 scoreboard-full-border td-players-new'}
                                        id={'td_player_right_1'}
                                        style={{ verticalAlign: 'middle' }}
                                    >
                                        <Image
                                            src={ShuttleImage}
                                            id={'image-playerR1'}
                                            className={
                                                playerPosition !== undefined
                                                    ? shuttlePosition === playerPosition[1][0]
                                                        ? 'shuttle'
                                                        : 'not-shuttle'
                                                    : 'not-shuttle'
                                            }
                                            style={{ float: 'left' }}
                                        />
                                        {match !== undefined && playerPosition !== undefined
                                            ? match?.player_guest[playerPosition[1][0] % 2]
                                            : 'Player right 1'}
                                    </td>
                                    <td className={'width6 scoreboard-full-border'} />
                                </tr>
                                <tr className={'height42Percentage'}>
                                    <td className={'width6 scoreboard-full-border'} />
                                    <td
                                        className={'width30 scoreboard-full-border td-players-new'}
                                        id={'td_player_left_2'}
                                        style={{ verticalAlign: 'middle' }}
                                    >
                                        {match !== undefined && playerPosition !== undefined
                                            ? match?.player_home[playerPosition[0][1]]
                                            : 'Player left 2'}
                                        <Image
                                            src={ShuttleImage}
                                            id={'image-playerL2'}
                                            className={
                                                playerPosition !== undefined
                                                    ? shuttlePosition === playerPosition[0][1]
                                                        ? 'shuttle'
                                                        : 'not-shuttle'
                                                    : 'not-shuttle'
                                            }
                                            style={{ float: 'right', transform: 'scale(-1, -1)' }}
                                        />
                                    </td>
                                    <td colSpan={2} className={'width28 scoreboard-bottom-border'} />
                                    <td
                                        className={'width30 scoreboard-full-border td-players-new'}
                                        id={'td_player_right_2'}
                                        style={{ verticalAlign: 'middle' }}
                                    >
                                        <Image
                                            src={ShuttleImage}
                                            id={'image-playerR2'}
                                            className={
                                                playerPosition !== undefined
                                                    ? shuttlePosition === playerPosition[1][1]
                                                        ? 'shuttle'
                                                        : 'not-shuttle'
                                                    : 'not-shuttle'
                                            }
                                            style={{ float: 'left', transform: 'scale(1, -1)' }}
                                        />
                                        {match !== undefined && playerPosition !== undefined
                                            ? match?.player_guest[playerPosition[1][1] % 2]
                                            : 'Player right 2'}
                                    </td>
                                    <td className={'width6 scoreboard-full-border'} />
                                </tr>
                                <tr className={'height8Percentage'}>
                                    <td className={'width6 scoreboard-full-border'} />
                                    <td className={'width30 scoreboard-full-border'} />
                                    <td colSpan={2} className={'width28 scoreboard-full-border'} />
                                    <td className={'width30 scoreboard-full-border'} />
                                    <td className={'width6 scoreboard-full-border'} />
                                </tr>
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
}

export default Scoreboard;
